import * as XLSX from 'xlsx';

/**
 * Export data to Excel file with formatting
 * @param {Array} data - Array of objects to export
 * @param {string} filename - Name of the file (without .xlsx extension)
 * @param {string} sheetName - Name of the Excel sheet
 * @param {Object} options - Optional configuration
 */
export const exportToExcel = (data, filename, sheetName = 'Data', options = {}) => {
  if (!data || data.length === 0) {
    alert('No data to export');
    return;
  }

  // Create workbook and worksheet
  const workbook = XLSX.utils.book_new();
  const worksheet = XLSX.utils.json_to_sheet(data);

  // Auto-size columns
  const columnWidths = [];
  const keys = Object.keys(data[0]);
  
  keys.forEach((key, index) => {
    const maxLength = Math.max(
      key.length,
      ...data.map(row => {
        const value = row[key];
        if (value === null || value === undefined) return 0;
        return String(value).length;
      })
    );
    columnWidths[index] = { wch: Math.min(maxLength + 2, 50) }; // Max width 50
  });
  
  worksheet['!cols'] = columnWidths;

  // Add worksheet to workbook
  XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);

  // Generate Excel file and trigger download
  XLSX.writeFile(workbook, `${filename}.xlsx`);
};

/**
 * Export multiple sheets to a single Excel file
 * @param {Array} sheets - Array of {data, sheetName} objects
 * @param {string} filename - Name of the file (without .xlsx extension)
 */
export const exportMultiSheetExcel = (sheets, filename) => {
  if (!sheets || sheets.length === 0) {
    alert('No data to export');
    return;
  }

  const workbook = XLSX.utils.book_new();

  sheets.forEach(({ data, sheetName }) => {
    if (data && data.length > 0) {
      const worksheet = XLSX.utils.json_to_sheet(data);
      
      // Auto-size columns
      const columnWidths = [];
      const keys = Object.keys(data[0]);
      
      keys.forEach((key, index) => {
        const maxLength = Math.max(
          key.length,
          ...data.map(row => {
            const value = row[key];
            if (value === null || value === undefined) return 0;
            return String(value).length;
          })
        );
        columnWidths[index] = { wch: Math.min(maxLength + 2, 50) };
      });
      
      worksheet['!cols'] = columnWidths;
      
      XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
    }
  });

  XLSX.writeFile(workbook, `${filename}.xlsx`);
};

/**
 * Flatten nested objects for Excel export (similar to CSV but more Excel-friendly)
 * @param {Object} obj - Object to flatten
 * @param {string} prefix - Prefix for nested keys
 */
export const flattenObjectForExcel = (obj, prefix = '') => {
  return Object.keys(obj).reduce((acc, key) => {
    const pre = prefix.length ? prefix + '_' : '';
    
    if (typeof obj[key] === 'object' && obj[key] !== null && !Array.isArray(obj[key])) {
      Object.assign(acc, flattenObjectForExcel(obj[key], pre + key));
    } else if (Array.isArray(obj[key])) {
      // For Excel, join arrays with semicolon for better readability
      acc[pre + key] = obj[key].join('; ');
    } else if (obj[key] instanceof Date) {
      // Format dates nicely
      acc[pre + key] = obj[key].toLocaleDateString();
    } else {
      acc[pre + key] = obj[key];
    }
    
    return acc;
  }, {});
};

/**
 * Format data for Excel export with better column names
 * @param {Array} data - Raw data array
 * @param {Object} columnMapping - Map of original keys to readable names
 */
export const formatDataForExcel = (data, columnMapping = {}) => {
  return data.map(row => {
    const formatted = {};
    Object.keys(row).forEach(key => {
      const readableName = columnMapping[key] || key
        .replace(/_/g, ' ')
        .replace(/\b\w/g, c => c.toUpperCase());
      formatted[readableName] = row[key];
    });
    return formatted;
  });
};
